import { Card } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IDiscount, IOrderItemInfo, ITaxInfo } from "../../../apis/useGetOrderDetail/types";
import { calculateTotalBcrsDeposit } from "../../../common/helpers/bcrsHelper";
import { displayCentsWithDollarSign, trimPercentZeros } from "../../../common/helpers/stringUtils";
import { GST_REGEX, SERVICE_CHARGE_REGEX, findTaxEntry } from "../../../common/helpers/taxHelper";
import Condition from "../../../components/Conditional/Condition";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import OrderConfirmationItem from "../OrderConfirmationItem";
import OrderConfirmationTakeawayItem from "../OrderConfirmationTakeawayItem";
import "./OrderDetailsCard.scss";

interface IOrderDetailsCardProps {
	orderItems: IOrderItemInfo[];
	subtotal_value: number;
	net_value: number;
	takeawayFee: number;
	tax?: ITaxInfo[];
	isTakeaway?: boolean;
	discounts?: IDiscount[];
}

const OrderDetailsCardWang: React.FC<IOrderDetailsCardProps> = ({
	orderItems,
	subtotal_value,
	net_value,
	takeawayFee,
	tax,
	isTakeaway = false,
	discounts = [],
}) => {
	const { t } = useTranslation();
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);

	// Calculate total BCRS deposit amount using helper
	const totalBcrsDeposit = useMemo(() => {
		return calculateTotalBcrsDeposit(orderItems, bcrs_deposit_rate);
	}, [orderItems, bcrs_deposit_rate]);

	const serviceChargeTax = findTaxEntry(tax, SERVICE_CHARGE_REGEX);
	const gstTax = findTaxEntry(tax, GST_REGEX);

	return (
		<Card className="OrderDetailsCard">
			<div className="OrderDetailsSection" id="OrderDetailsReceipt">
				<ul className="CartContainer">
					{orderItems.map((item) => (
						<OrderConfirmationItem key={item.item_order_id} item={item} isTakeaway={isTakeaway} />
					))}

					<Condition check={takeawayFee > 0}>
						<OrderConfirmationTakeawayItem takeawayFee={takeawayFee} />
					</Condition>
				</ul>

				<div className="OrderSummary">
					<Condition check={discounts.length > 0}>
						{discounts.map((discount, index) => (
							<div key={index} className="SummaryRow DiscountRow">
								<span className="SummaryLabel">{trimPercentZeros(discount.description)}</span>
								<span className="SummaryValue DiscountValue">
									-{displayCentsWithDollarSign(discount.amount)}
								</span>
							</div>
						))}
					</Condition>
					<div className="SummaryRow">
						<span className="SummaryLabel">{t("orderConfirmation.subtotal")}</span>
						<span className="SummaryValue">{displayCentsWithDollarSign(subtotal_value)}</span>
					</div>
					<Condition check={!!serviceChargeTax}>
						<div className="SummaryRow ServiceChargeRow">
							<span className="SummaryLabel">{serviceChargeTax?.name}</span>
							<span className="SummaryValue">
								{displayCentsWithDollarSign(serviceChargeTax?.amount ?? 0)}
							</span>
						</div>
					</Condition>
					<Condition check={!!gstTax}>
						<div className="SummaryRow GstRowWang">
							<span className="SummaryLabel">{gstTax?.name}</span>
							<span className="SummaryValue">
								{displayCentsWithDollarSign(gstTax?.amount ?? 0)}
							</span>
						</div>
					</Condition>
					<Condition check={totalBcrsDeposit > 0}>
						<div className="SummaryRowGST">
							<span className="SummaryLabel">{t("orderConfirmation.bcrs.totalDeposit")}</span>
							<span className="SummaryValue">{displayCentsWithDollarSign(totalBcrsDeposit)}</span>
						</div>
						<div className="SummaryRowBCRS BCRSRow">
							<span className="BcrsDisclaimer">{t("cartPage.bcrs.disclaimer")}</span>
						</div>
					</Condition>
					<div className="SummaryRow FinalTotalRow">
						<span className="SummaryLabel">{t("orderConfirmation.total")}</span>
						<span className="SummaryValue">{displayCentsWithDollarSign(net_value)}</span>
					</div>
				</div>
			</div>
		</Card>
	);
};

export default OrderDetailsCardWang;
